import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Lock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch {
      toast.error("Could not load quotes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setUser(data);
        loadQuotes();
      })
      .catch(() => setUser(false));
  }, [loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      loadQuotes();
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unseen = quotes.filter((q) => !q.seen).length;

  const markReviewed = async () => {
    await axios.post(`${API}/admin/quotes/mark-seen`, {}, { withCredentials: true });
    loadQuotes();
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setQuotes([]);
  };

  if (user === null) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-brand" size={32} />
      </main>
    );
  }

  if (user === false) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-navy flex items-center justify-center px-5 pt-16">
        <div className="w-full max-w-md bg-white rounded-md p-8 sm:p-10">
          <div className="w-12 h-12 bg-brand-subtle rounded-sm flex items-center justify-center mb-6">
            <Lock size={22} className="text-brand" />
          </div>
          <h1 className="font-display font-bold text-2xl text-navy mb-1">Admin Sign In</h1>
          <p className="text-sm text-slate-500 mb-7">HLM Moving quote dashboard</p>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Email</label>
              <input data-testid="admin-login-email-input" type="email" required className={inputCls}
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@hlmmoving.com" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-navy/70 mb-1.5">Password</label>
              <input data-testid="admin-login-password-input" type="password" required className={inputCls}
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{error}</p>
            )}
            <button data-testid="admin-login-submit-button" type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-bold uppercase tracking-wide px-6 py-3.5 rounded-sm transition-colors">
              Sign In
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-background pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-1">Admin</p>
            <h1 className="font-display font-bold tracking-tight text-3xl text-navy">Quote Requests</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-slate-500">{quotes.length} saved submission{quotes.length === 1 ? "" : "s"}</p>
              {unseen > 0 && (
                <span data-testid="admin-new-badge" className="bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-sm">
                  {unseen} NEW
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a data-testid="admin-export-csv-button" href={`${API}/admin/quotes/export`}
              className="inline-flex items-center gap-2 border border-slate-300 text-navy hover:border-brand hover:text-brand text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">
              <Download size={15} /> Download CSV
            </a>
            {unseen > 0 && (
              <button data-testid="admin-mark-reviewed-button" onClick={markReviewed}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">
                Mark Reviewed
              </button>
            )}
            <button data-testid="admin-refresh-button" onClick={loadQuotes}
              className="inline-flex items-center gap-2 border border-slate-300 text-navy hover:border-brand hover:text-brand text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout-button" onClick={logout}
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors">
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>

        {quotes.length === 0 ? (
          <div data-testid="quotes-empty-state" className="bg-white border border-slate-200 rounded-md p-16 text-center">
            <p className="text-slate-500">No quote requests yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table data-testid="quotes-table" className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  {["Submitted", "Name", "Company", "Contact", "Service", "Route", "Move Date", "Details"].map((h) => (
                    <th key={h} className="px-4 py-3 font-display font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-slate-100 hover:bg-brand-subtle/50 align-top">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {q.created_at ? new Date(q.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-navy">
                      {!q.seen && (
                        <span className="inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm mr-2 align-middle">NEW</span>
                      )}
                      {q.full_name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{q.company || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div>{q.email}</div>
                      <div className="text-slate-400">{q.phone}</div>
                    </td>
                    <td className="px-4 py-3"><span className="bg-brand-subtle text-brand-hover text-xs font-semibold px-2 py-1 rounded-sm whitespace-nowrap">{q.service_type}</span></td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{q.origin_zip} → {q.destination_zip}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{q.move_date}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[240px]">{q.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
